import { formatI18nDate } from 'utils/i18n-format-date';

/**
 * Formats a timestamp as a relative time string (e.g., "2 days ago").
 * 
 * This is a presentation concern - it handles how we display time to users.
 * Business rules like "is this listing new?" belong in isNewListing().
 * 
 * @param timestamp - The timestamp to format
 * @param locale - The locale for formatting (e.g., 'en-CA')
 * @returns Formatted string or null if timestamp is invalid
 */
export function formatRecency(timestamp: Date | string | null, locale: string): string | null {
  if (!timestamp) {
    return null;
  }

  const formatted = formatI18nDate(timestamp, 'relative', locale);
  
  if (!formatted) {
    return null;
  }

  // Capitalize first letter
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
}

/**
 * Calculates the number of days since a timestamp.
 * Internal helper - not exported.
 * 
 * @param timestamp - The timestamp to calculate from
 * @returns Number of days, or null if timestamp is invalid
 */
function getDaysSince(timestamp: Date | null): number | null {
  if (!timestamp) {
    return null;
  }
  return Math.floor((Date.now() - timestamp.getTime()) / (1000 * 60 * 60 * 24));
}

/**
 * Checks if a timestamp is valid (not null and not in the future).
 * 
 * @param timestamp - The timestamp to validate
 * @returns true if timestamp exists and is not in the future
 */
export function isValidTimestamp(timestamp: Date | null): boolean {
  if (!timestamp) {
    return false;
  }
  return new Date() >= timestamp;
}

/**
 * Checks if a listing qualifies as "new" based on its added date.
 * 
 * Business rule: Listings added within the threshold are considered "new".
 * Default threshold is 30 days (for general "new" badge).
 * 
 * Note: BadgeDisplayPolicy uses a stricter 7-day threshold for the badge.
 * 
 * @param addedAt - When the listing was added
 * @param thresholdDays - Number of days to consider "new" (default: 30)
 * @returns true if listing is within the threshold
 */
export function isNewListing(addedAt: Date | null, thresholdDays: number = 30): boolean {
  const days = getDaysSince(addedAt);
  return days !== null && days <= thresholdDays;
}
